import React, { useState, useEffect } from 'react';
import { Fab, useScrollTrigger, Zoom } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { motion } from 'framer-motion';

interface ScrollToTopProps {
  threshold?: number;
  scrollDuration?: number;
}

const ScrollToTop: React.FC<ScrollToTopProps> = ({ 
  threshold = 300, 
  scrollDuration = 500 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  
  // Use MUI's useScrollTrigger to determine when to show the button
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: threshold,
  });
  
  useEffect(() => {
    setIsVisible(trigger);
  }, [trigger]);
  
  const handleClick = () => {
    const startTime = performance.now();
    const startPosition = window.pageYOffset;
    
    const scrollStep = (timestamp: number) => {
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / scrollDuration, 1);
      
      // Easing function for smooth scrolling
      const easeInOutCubic = (t: number) => 
        t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      
      window.scrollTo(0, startPosition * (1 - easeInOutCubic(progress)));
      
      if (progress < 1) {
        window.requestAnimationFrame(scrollStep);
      }
    };
    
    window.requestAnimationFrame(scrollStep);
  };
  
  return (
    <Zoom in={isVisible}>
      <motion.div
        style={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          zIndex: 1000,
        }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <Fab
          color="primary"
          size="medium"
          aria-label="scroll back to top"
          onClick={handleClick}
          sx={{
            boxShadow: '0 4px 20px rgba(57, 255, 20, 0.3)',
          }}
        >
          <KeyboardArrowUpIcon />
        </Fab>
      </motion.div>
    </Zoom>
  );
};

export default ScrollToTop; 